import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import {User} from '../types/auth';
import {
  getStoredUser,
  login as authLogin,
  logout as authLogout,
  deleteAccount as authDeleteAccount,
  requestAccountDeletion as authRequestAccountDeletion,
  verifyToken,
} from '../services/auth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{success: boolean; message?: string}>;
  logout: () => Promise<void>;
  deleteAccount: (code: string) => Promise<{success: boolean; message?: string}>;
  requestAccountDeletion: () => Promise<{success: boolean; message?: string}>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStoredUser();
  }, []);

  const loadStoredUser = async () => {
    try {
      // First try to get stored user
      const storedUser = await getStoredUser();
      if (storedUser) {
        setUser(storedUser);
        // Then verify token in background
        verifyToken().then(freshUser => {
          if (freshUser) {
            setUser(freshUser);
          } else {
            setUser(null);
          }
        });
      }
    } catch (error) {
      console.error('Error loading stored user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const result = await authLogin(email, password);
    if (result.success && result.user) {
      setUser(result.user);
    }
    return {success: result.success, message: result.message};
  };

  const logout = async () => {
    await authLogout();
    setUser(null);
  };

  const deleteAccount = async (code: string) => {
    const result = await authDeleteAccount(code);
    if (result.success) {
      setUser(null);
    }
    return result;
  };

  const requestAccountDeletion = async () => {
    return await authRequestAccountDeletion();
  };

  const refreshUser = async () => {
    const freshUser = await verifyToken();
    setUser(freshUser);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        deleteAccount,
        requestAccountDeletion,
        refreshUser,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
